import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-expenses-parity';
import { call, type Outcome } from './api';
import type { Report } from './types';

/** #915's routes: the note's own files, its projects and client, the expense's internal class. */

export type ReportDocument = components['schemas']['ReportDocument'];
export type CatalogItem = components['schemas']['CatalogItem'];
export type InternalClassCode = NonNullable<components['schemas']['InternalClass']>;
export type AllocationPatch = components['schemas']['ReportAllocationPatch'];
export type DocumentsListing = { items?: ReportDocument[]; can?: { add?: boolean } };

export function listNoteDocuments(reportId: string): Promise<Outcome<DocumentsListing>> {
  return call<DocumentsListing>(() =>
    apiClient.GET('/v1/expenses/reports/{reportId}/documents', {
      params: { path: { reportId } },
    }),
  );
}

/**
 * Adds one PDF to the note as the raw body, as an expense's document is sent (release 1). The label
 * rides percent-encoded in `X-Document-Label`, so any UTF-8 survives the header.
 */
export function addNoteDocument(
  reportId: string,
  pdf: Blob,
  source: 'images' | 'pdf',
  label: string,
): Promise<Outcome<ReportDocument>> {
  const trimmed = label.trim();
  return call<ReportDocument>(() =>
    apiClient.PUT('/v1/expenses/reports/{reportId}/documents', {
      params: {
        path: { reportId },
        header: {
          'X-Document-Source': source,
          ...(trimmed === '' ? {} : { 'X-Document-Label': encodeURIComponent(trimmed) }),
        },
      },
      body: pdf as unknown as string,
      bodySerializer: (body: unknown) => body as BodyInit,
      headers: { 'Content-Type': 'application/pdf' },
    }),
  );
}

export function getNoteDocument(reportId: string, documentId: string): Promise<Outcome<Blob>> {
  return call<Blob>(() =>
    apiClient.GET('/v1/expenses/reports/{reportId}/documents/{documentId}', {
      params: { path: { reportId, documentId } },
      parseAs: 'blob',
    }),
  );
}

export function removeNoteDocument(
  reportId: string,
  documentId: string,
): Promise<Outcome<unknown>> {
  return call<unknown>(() =>
    apiClient.DELETE('/v1/expenses/reports/{reportId}/documents/{documentId}', {
      params: { path: { reportId, documentId } },
    }),
  );
}

/** Sets the note's projects and client (release 2's PATCH, with #915's fields only). */
export function patchAllocation(
  reportId: string,
  version: string,
  patch: AllocationPatch,
): Promise<Outcome<Report>> {
  return call<Report>(() =>
    apiClient.PATCH('/v1/expenses/reports/{reportId}', {
      params: { path: { reportId }, header: { 'If-Match': version } },
      body: patch as never,
    }),
  );
}

export async function listCatalog(kind: 'projects' | 'clients'): Promise<Outcome<CatalogItem[]>> {
  const out = await call<{ items: CatalogItem[] }>(() =>
    kind === 'projects'
      ? apiClient.GET('/v1/expenses/projects')
      : apiClient.GET('/v1/expenses/clients'),
  );
  return out.ok ? { ok: true, value: Array.isArray(out.value.items) ? out.value.items : [] } : out;
}

/** The quick class change (Gestione and the note's approvers); null is «Nessuna». */
export function setExpenseClass<T>(
  expenseId: string,
  internalClass: InternalClassCode | null,
): Promise<Outcome<T>> {
  return call<T>(() =>
    apiClient.PATCH('/v1/expenses/{expenseId}/class', {
      params: { path: { expenseId } },
      body: { internalClass },
    }),
  );
}
